"use client";

import { Children, isValidElement, useEffect, useState, type ReactNode } from "react";
import { AUFBAU_KLASSE } from "../ui/Aufbau";
import { HUELLE_KNOEPFE, huellenStil } from "./huellen-auftritt";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { useThemeModeSetter, useEffectiveThemeMode, useDesignPanel } from "../../providers/ColorThemeProvider";
import { cn } from "../../lib/utils";
import { TOPNAV_ICON_BTN } from "./NotificationBell";

/**
 * Erzwungene Metrik fuer ALLE Steuerelemente im App-Header — dasselbe Muster,
 * das `toolbarControlCls` (ui/PageHeader.tsx) im Content-Header durchsetzt.
 *
 * Warum noetig: Apps reichten ihre Header-Icons frueher ohne Vertrag durch
 * (affiliate eine nackte Glocke, mail einen eigenen gap-1-Wrapper), und der
 * Theme-Toggle lag ausserhalb ihres Containers. Ergebnis waren drei
 * verschiedene Abstaende in derselben Zeile: 4px / 16px / 4px.
 *
 * `[&_a]` ist bewusst dabei: `<Button asChild><Link>` rendert ein <a>, kein
 * <button> — genau daran scheitert die Normalisierung im Content-Header heute.
 *
 * `:where(:not(.jw-pop_*))` nimmt Teilbaeume von `MenuPopover` (Klasse
 * `jw-pop`) aus: `MenuPopover` rendert kein Portal, haengt also im DOM direkt
 * unter dem Cluster-Kind, das es oeffnet — ohne den Ausschluss wuerde die
 * Metrik bis in jede Popover-Zeile durchgreifen (Belegfall: NotificationBell
 * im Popup-Modus, s. TopNavActions-Doc-Kommentar unten). `:where()` haelt den
 * Spezifitaets-Beitrag des Ausschlusses bei 0 — am Verhalten fuer alles
 * ausserhalb von `.jw-pop` aendert sich dadurch nichts.
 */
export const TOPNAV_ACTION_CLS =
  "[&_button:where(:not(.jw-pop_*))]:p-2.5 [&_a:where(:not(.jw-pop_*))]:p-2.5 [&_button:where(:not(.jw-pop_*))]:rounded-[var(--radius)] [&_a:where(:not(.jw-pop_*))]:rounded-[var(--radius)] [&_button:where(:not(.jw-pop_*))>svg]:size-[18px] [&_a:where(:not(.jw-pop_*))>svg]:size-[18px]";

/**
 * Der geteilte Theme-Umschalter. Vorher lag er viermal im Code (TopNav,
 * HubLauncher, StatusDashboard, ApiExplorer) mit abweichendem Padding und
 * abweichender Deckkraft (/50 vs /70).
 */
export function ThemeToggleButton({ className }: { className?: string }) {
  useTheme();
  const setMode = useThemeModeSetter();
  // Bewusst NICHT `theme` aus next-themes: das folgt erst, wenn die Welle am
  // Ende `setTheme()` ruft. Das Icon kippte dadurch eine halbe Sekunde zu spaet,
  // und ein zweiter schneller Klick berechnete aus demselben veralteten Wert
  // NOCHMAL dasselbe Ziel — zweite Welle ohne Wechsel, sichtbar als Fehler.
  const mode = useEffectiveThemeMode();
  const { setOpen: setDesignPanelOpen, available: designPanelAvailable } = useDesignPanel();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Platzhalter gleicher Groesse -> kein Layout-Sprung nach der Hydration.
  // Berechnung: 18px Icon (aus [&_button>svg]:size-[18px]) + 2×10px Padding (aus p-2.5 in TOPNAV_ICON_BTN) = 38px.
  // Aenderungen an TOPNAV_ICON_BTN hier syncen.
  //
  // ER TRAEGT DAS SYMBOL, UND ZWAR AUS CSS. Leer war er bis 0.289.0, und das
  // hat den ganzen Auftritt der Kopfzeile an dieser einen Stelle aufgehoben:
  // die Huelle drumherum (TopNavActions.gestaffelt) blendet planmaessig bei
  // 120 ms ein — nur ist nichts drin, was man sehen koennte. Der Knopf selbst
  // erschien dann schlagartig mit der Hydration. Pixelgemessen in ai-chat am
  // 2026-08-22: alle anderen Zeichen der Huelle lagen zwischen +0 und +319 ms,
  // dieser bei +448 ms — und ohne jede Bewegung.
  //
  // `resolvedTheme` aus next-themes ist beim ersten Rendern `undefined`, der
  // Modus laesst sich hier also nicht lesen. Die Klasse `dark` steht aber schon
  // aus dem Server-Markup auf `<html>` (theme-ssr.ts liest den Cookie), und die
  // reicht CSS. Der Tausch gegen den echten Knopf ist danach unsichtbar:
  // gleiches Symbol, gleiche Stelle, gleiche Farbe.
  if (!mounted)
    return (
      <span
        aria-hidden
        // Kein `hover:` wie in TOPNAV_ICON_BTN: der Platzhalter ist nicht
        // bedienbar und darf sich nicht so anfuehlen.
        className={cn(
          "inline-flex items-center justify-center p-2.5 rounded-[var(--radius)] text-muted-foreground/50",
          className,
        )}
      >
        <Sun size={18} className="hidden dark:block" />
        <Moon size={18} className="dark:hidden" />
      </span>
    );

  const isDark = mode === "dark";

  // Rechtsklick oeffnet das Design-Panel — aber NUR, wenn irgendwo ein Renderer
  // (TopNav/HubLauncher) gemountet ist. Der Schalter sitzt auch auf Seiten ohne
  // Panel (StatusDashboard, ApiExplorer, MobileNavAccountFooter); ohne diese
  // Pruefung wuerde dort das native Kontextmenue unterdrueckt, ohne dass je
  // etwas erscheint — eine Sackgasse. Nur Desktop: mobil bleibt das native
  // Kontextmenue erreichbar, dort fuehrt der Weg ueber den Drawer.
  const onContextMenu = (e: React.MouseEvent) => {
    if (!designPanelAvailable) return;
    if (window.innerWidth < 768) return;
    e.preventDefault();
    setDesignPanelOpen(true);
  };

  return (
    <button
      onClick={(e) => setMode(isDark ? "light" : "dark", e.currentTarget)}
      onContextMenu={onContextMenu}
      data-jw-design-trigger=""
      aria-label={isDark ? "Zu hellem Design wechseln" : "Zu dunklem Design wechseln"}
      title={
        designPanelAvailable
          ? "Hell/Dunkel umschalten · Rechtsklick: Design"
          : "Hell/Dunkel umschalten"
      }
      className={cn(TOPNAV_ICON_BTN, className)}
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

export interface TopNavActionsProps {
  children?: ReactNode;
  /**
   * Die Steuerelemente treten beim Oeffnen der App einzeln auf, 40 ms
   * auseinander, beginnend bei `HUELLE_KNOEPFE`. Vorgabe `false`: derselbe
   * Cluster steht auch im Fuss der Mobil-Navigation und im Hub, wo es keinen
   * Seiten-Aufbau gibt.
   */
  auftritt?: boolean;
  className?: string;
}

/**
 * EIN Cluster fuer alle Header-Steuerelemente. Der einheitliche Abstand ist
 * dadurch eine Eigenschaft der Komponente statt einer Konvention, die 16 Apps
 * einhalten muessen: Apps reichen nur noch WAS hinein, nie WIE es aussieht.
 *
 * Kein eigener Rahmen, kein eigenes Aussen-Padding: der einzige Aufrufer, der
 * das je brauchte (MobileNavAccountFooter), traegt Trennlinie und Padding
 * selbst an seiner Zeile, weil er sie mit einem Geschwister-Element teilt.
 *
 * `shrink-0`: der Cluster gibt niemals Breite ab — nachgeben darf nur die
 * Wortmarke (siehe AppSwitcher/AppWordmark). Bedienelemente verschwinden nicht.
 *
 * `h-full`: manche Kinder (z.B. `NotificationBell`) positionieren ihr eigenes
 * Popover per `top-full` relativ zu einem eigenen `h-full`-Wrapper. Damit
 * dieses innere `h-full` ueberhaupt einen Pixelwert ergibt statt `auto`,
 * braucht schon dieser Container hier eine definierte Hoehe. Avatar/Profil-
 * Popup haengen deshalb bewusst NICHT hier drin (siehe TopNav.tsx) — sie
 * bauen ihre eigene, direkt am Header haengende `h-full`-Kette.
 *
 * Kein eigenes Rand-Padding zum naechsten Geschwister: was hinter diesem
 * Cluster im Header folgt (z.B. der Avatar-Block), muss den Abstand selbst
 * mitbringen — sonst waere der Wert vom jeweiligen Nachbarn abhaengig und
 * nicht mehr Eigenschaft dieser Komponente.
 *
 * Regel: Elemente mit eigener Groessenlogik (Avatare, Badges, Text-Pills)
 * gehoeren weiterhin NEBEN den Cluster, nicht hinein — TOPNAV_ACTION_CLS
 * greift per Descendant-Selektor bis in solche Teilbaeume durch und ist dort
 * mit eigenen Utilities nicht ueberstimmbar (Spezifitaet 0,1,1). Belege:
 * TopNav-Avatar, HubLauncher-UserMenu, ApiExplorer-Pills, Status-
 * SubscribeButton. `.jw-pop`-Popover-Teilbaeume (MenuPopover) sind seit der
 * `:where(:not(.jw-pop_*))`-Ausnahme in TOPNAV_ACTION_CLS selbst ausgenommen
 * und muessen NICHT mehr verschoben werden (Belegfall: NotificationBell).
 */
export function TopNavActions({ children, auftritt = false, className }: TopNavActionsProps) {
  return (
    <div
      className={cn("flex items-center gap-1 h-full shrink-0 pl-1", TOPNAV_ACTION_CLS, className)}
    >
      {auftritt ? gestaffelt(children) : children}
    </div>
  );
}

/**
 * Legt je Kind eine Huelle mit eigener Verzoegerung an — VON RECHTS NACH LINKS.
 *
 * Die Kopfzeile laeuft von aussen nach innen: das App-Zeichen ganz links und
 * der Konto-Knopf ganz rechts beginnen gemeinsam, dann fuellt sich die Zeile
 * zur Mitte hin. Rechts heisst das gegen die DOM-Reihenfolge — das letzte Kind
 * steht dem Konto am naechsten und kommt zuerst. Deshalb der gedrehte Index.
 *
 * Bis 0.291.0 lief es in DOM-Reihenfolge, und weil das Konto zusaetzlich fest
 * auf 300 ms stand, entstand in jeder App mit nur einem Steuerelement ein Loch:
 * Knopf bei 120 ms, 180 ms nichts, dann das Konto.
 *
 * WARUM EINE HUELLE UND NICHT EIN `> *`-SELEKTOR IN CSS: die Regel muesste in
 * `globals.css` stehen, und die Verzoegerung je Position braeuchte dort eine
 * `nth-child`-Zeile pro moeglichem Knopf — eine Zahl, die niemand nachzieht,
 * wenn eine App einen sechsten hinzufuegt.
 *
 * `h-full inline-flex items-center` ist Pflicht, nicht Kosmetik: `NotificationBell`
 * setzt sein Popover per `top-full` gegen einen eigenen `h-full`-Wrapper, und
 * eine Huelle ohne Hoehe dazwischen liesse den zu `auto` zusammenfallen.
 *
 * Nicht-Elemente (Text, `false`, `null`) werden unveraendert durchgereicht —
 * eine Huelle um `false` waere ein leeres Flex-Kind und damit ein zusaetzlicher
 * `gap-1`.
 */
function gestaffelt(children: ReactNode): ReactNode {
  // Erst zaehlen: der Index laeuft von rechts, und dafuer muss die Zahl der
  // echten Elemente vorher feststehen. `Children.count` zaehlt auch `false`
  // und `null` mit, die hier keine Huelle bekommen.
  let anzahl = 0;
  Children.forEach(children, (kind) => {
    if (isValidElement(kind)) anzahl += 1;
  });
  let i = 0;
  return Children.map(children, (kind) => {
    if (!isValidElement(kind)) return kind;
    const stil = huellenStil(HUELLE_KNOEPFE, anzahl - 1 - i);
    i += 1;
    return (
      <span className={cn("inline-flex h-full items-center", AUFBAU_KLASSE)} style={stil}>
        {kind}
      </span>
    );
  });
}
